/* PromoVet: DL price in the native price row; customer pricing uses one-time purchase. */
(() => {
  const elementName = 'pv-dl-product-pricing';
  if (customElements.get(elementName)) return;

  const timeout = (promise, milliseconds) => {
    let timer;
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('DL pricing timed out')), milliseconds);
      })
    ]).finally(() => clearTimeout(timer));
  };

  class PromoVetDLPricing extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.connect());
    }

    connect() {
      if (!this.isConnected || this.abortController) return;
      this.form = this.closest('form') || document.getElementById(this.dataset.formId);
      this.scope = this.form?.closest('.card');
      if (!this.form || !this.scope || !this.priceElement()) return;
      this.abortController = new AbortController();
      this.scope.setAttribute('data-pv-dl-product-scope', '');
      this.state = 'pending';
      this.result = null;
      this.requestNumber = 0;
      this.form.pvDLPricingController = this;
      const options = { signal: this.abortController.signal };

      ['change', 'input', 'variant:change', 'product:rerender'].forEach((name) => {
        this.form.addEventListener(name, () => this.schedule(), options);
      });
      document.addEventListener('dollarlabs:pricing:ready', () => {
        if (this.state === 'unavailable') this.key = null;
        this.schedule();
      }, options);
      this.form.addEventListener('submit', (event) => this.guardPurchase(event),
        { ...options, capture: true });
      this.form.addEventListener('click', (event) => {
        if (event.target.closest('buy-button, button[type="submit"], input[type="submit"], .shopify-payment-button')) {
          this.guardPurchase(event);
        }
      }, { ...options, capture: true });

      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.form, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['value', 'name', 'disabled', 'data-variant-id']
      });
      this.refresh();
    }

    disconnectedCallback() {
      this.requestNumber = (this.requestNumber || 0) + 1;
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
      clearTimeout(this.scheduled);
      this.restorePlans();
      const scope = this.scope;
      const form = this.form;
      queueMicrotask(() => {
        if (form?.pvDLPricingController === this) {
          delete form.pvDLPricingController;
          scope?.removeAttribute('data-pv-dl-hide-subscriptions');
          scope?.removeAttribute('data-pv-dl-product-scope');
        }
      });
    }

    priceElement() {
      return this.form?.querySelector('[data-pv-dl-price]');
    }

    selection() {
      const variantId = String(this.form.elements.namedItem('id')?.value || '');
      const quantity = Number(this.form.elements.namedItem('quantity')?.value || 1);
      return { variantId, quantity, key: `${variantId}:${quantity}` };
    }

    schedule() {
      clearTimeout(this.scheduled);
      this.scheduled = setTimeout(() => this.refresh(), 0);
    }

    sdkReady() {
      const ready = () => window.DollarlabsPricingReady &&
        typeof window.DollarlabsGetBestPrice === 'function' &&
        typeof window.DollarlabsGetPricingString === 'function';
      if (ready()) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const signal = this.abortController.signal;
        let poll;
        let deadline;
        const finish = (error) => {
          clearInterval(poll);
          clearTimeout(deadline);
          document.removeEventListener('dollarlabs:pricing:ready', check);
          signal.removeEventListener('abort', aborted);
          error ? reject(error) : resolve();
        };
        const check = () => { if (ready()) finish(); };
        const aborted = () => finish(new Error('Product form was removed'));
        document.addEventListener('dollarlabs:pricing:ready', check);
        signal.addEventListener('abort', aborted, { once: true });
        poll = setInterval(check, 200);
        deadline = setTimeout(() => finish(new Error('DL SDK unavailable')), 10000);
        check();
      });
    }

    async refresh() {
      if (!this.isConnected || !this.abortController) return;
      const selection = this.selection();
      if (this.key === selection.key) {
        this.paint();
        return;
      }
      this.key = selection.key;
      this.result = null;
      this.state = 'pending';
      const requestNumber = ++this.requestNumber;
      this.paint();
      if (!selection.variantId || !Number.isInteger(selection.quantity) || selection.quantity < 1) {
        this.state = 'invalid';
        this.paint();
        return;
      }
      try {
        await this.sdkReady();
        if (!this.current(requestNumber, selection.key)) return;
        const [price, rules] = await timeout(Promise.all([
          window.DollarlabsGetBestPrice(selection.variantId, selection.quantity),
          window.DollarlabsGetPricingString(selection.variantId)
        ]), 10000);
        if (!this.current(requestNumber, selection.key)) return;
        if (!['ready', 'no_rule'].includes(price?.status) ||
            !['ready', 'no_rule'].includes(rules?.status)) {
          throw new Error('DL pricing could not be confirmed');
        }
        const assignedPrice = rules.status === 'ready' && rules.found === true &&
          Array.isArray(rules.pricings) && rules.pricings.some((rule) =>
            rule.isCustomerTag === true && rule.isDefault === false &&
            typeof rule.pricingString === 'string' && rule.pricingString.trim() !== '');
        if (typeof price.formattedPrice !== 'string' || price.formattedPrice.trim() === '' ||
            !Number.isFinite(price.priceInCents)) {
          throw new Error('DL returned no usable price');
        }
        this.result = { price, assignedPrice };
        this.state = 'ready';
        this.paint();
      } catch (error) {
        if (!this.current(requestNumber, selection.key)) return;
        this.state = 'unavailable';
        this.paint();
        console.warn('[PromoVet DL pricing]', error.message);
      }
    }

    current(requestNumber, key) {
      return this.isConnected && this.abortController &&
        this.requestNumber === requestNumber && this.selection().key === key;
    }

    markSubscriptionBlocks() {
      this.scope.querySelectorAll('.shopify_subscriptions_app_container').forEach((widget) => {
        const block = widget.closest('.product-info__block-item') ||
          widget.closest('.shopify-block') || widget;
        block.setAttribute('data-pv-dl-subscription-block', '');
      });
      // Also cover a separate theme Text block using the heading shown in the screenshot.
      this.scope.querySelectorAll('[data-block-type="text"]').forEach((block) => {
        const heading = block.querySelector('h1, h2, h3, h4, h5, h6');
        if (/^Set Up Auto[-\u2011\u2013]Reorder$/i.test(heading?.textContent.trim() || '')) {
          block.setAttribute('data-pv-dl-subscription-block', '');
        }
      });
    }

    sellingPlanInputs() {
      return Array.from(this.form?.elements || []).filter((input) => input.name === 'selling_plan');
    }

    oneTimePurchase() {
      const { variantId } = this.selection();
      this.scope.querySelectorAll('.shopify_subscriptions_app_container section[data-variant-id]').forEach((section) => {
        if (section.dataset.variantId !== variantId) return;
        const radio = section.querySelector('input[data-radio-type="one_time_purchase"]');
        if (radio && !radio.checked) radio.click();
      });
    }

    clearPlans() {
      this.oneTimePurchase();
      this.sellingPlanInputs().forEach((input) => {
        if (!input.hasAttribute('data-pv-dl-original-disabled')) {
          input.setAttribute('data-pv-dl-original-disabled', String(input.disabled));
        }
        if (input.value !== '') input.value = '';
        if (!input.disabled) input.disabled = true;
      });
    }

    restorePlans() {
      this.sellingPlanInputs().forEach((input) => {
        if (!input.hasAttribute('data-pv-dl-original-disabled')) return;
        input.disabled = input.getAttribute('data-pv-dl-original-disabled') === 'true';
        input.value = '';
        input.removeAttribute('data-pv-dl-original-disabled');
      });
    }

    paint() {
      const priceElement = this.priceElement();
      if (!priceElement || this.selection().key !== this.key) return;
      this.markSubscriptionBlocks();
      const hideSubscriptions = this.state !== 'ready' || this.result?.assignedPrice;
      const wasHidden = this.scope.hasAttribute('data-pv-dl-hide-subscriptions');
      if (hideSubscriptions && !wasHidden) this.scope.setAttribute('data-pv-dl-hide-subscriptions', '');
      if (!hideSubscriptions && wasHidden) {
        if (this.sellingPlanInputs().some((input) => input.hasAttribute('data-pv-dl-original-disabled'))) {
          this.oneTimePurchase();
          this.restorePlans();
        }
        this.scope.removeAttribute('data-pv-dl-hide-subscriptions');
      }
      if (this.state === 'ready' && this.result.assignedPrice) this.clearPlans();

      let text = this.state === 'invalid' ? 'Select a valid quantity' : 'Loading price\u2026';
      let className = 'price';
      if (this.state === 'unavailable') text = 'Price unavailable';
      if (this.state === 'ready') {
        text = this.result.price.formattedPrice;
        if (Number(priceElement.dataset.comparePrice) > this.result.price.priceInCents) {
          className += ' price--highlight';
        }
      }
      if (priceElement.textContent !== text) priceElement.textContent = text;
      if (priceElement.className !== className) priceElement.className = className;

      const message = this.form.querySelector('[data-pv-dl-pricing-message]');
      if (message) {
        let content = '';
        if (this.state === 'unavailable') content = 'Pricing could not load. Refresh the page to retry.';
        if (this.state === 'ready' && this.result.assignedPrice &&
            priceElement.dataset.requiresSellingPlan === 'true') {
          content = 'This product requires a subscription. Contact us to order with your custom price.';
        }
        if (message.textContent !== content) message.textContent = content;
        if (message.hidden !== (content === '')) message.hidden = content === '';
      }
    }

    guardPurchase(event) {
      const priceElement = this.priceElement();
      const confirmed = this.state === 'ready' && this.selection().key === this.key &&
        priceElement?.dataset.variantId === this.selection().variantId;
      if (confirmed && this.result.assignedPrice) this.clearPlans();
      if (!confirmed || (this.result.assignedPrice && priceElement.dataset.requiresSellingPlan === 'true')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.schedule();
      }
    }
  }

  customElements.define(elementName, PromoVetDLPricing);
})();

/* Customer savings badges. Product badges reuse the confirmed visible price. */
(() => {
  const elementName = 'pv-dl-savings';
  if (customElements.get(elementName)) return;
  const cardPrices = new Map();
  const sdkReady = () => window.DollarlabsPricingReady &&
    typeof window.DollarlabsGetBestPrice === 'function';

  const cardPrice = (variantId) => {
    if (cardPrices.has(variantId)) return cardPrices.get(variantId);
    let timer;
    const promise = Promise.race([
      Promise.resolve().then(() => window.DollarlabsGetBestPrice(variantId, 1)),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Savings price lookup timed out')), 10000);
      })
    ]).then((price) => {
      if (!['ready', 'no_rule'].includes(price?.status) ||
          typeof price.formattedPrice !== 'string' || !price.formattedPrice.trim() ||
          !Number.isFinite(price.priceInCents) || price.priceInCents < 0) {
        throw new Error('Savings price is not confirmed');
      }
      return price.priceInCents;
    }).catch((error) => {
      if (cardPrices.get(variantId) === promise) cardPrices.delete(variantId);
      throw error;
    }).finally(() => clearTimeout(timer));
    cardPrices.set(variantId, promise);
    return promise;
  };

  class PromoVetDLSavings extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.connect());
    }

    connect() {
      if (!this.isConnected || this.observer) return;
      this.productMode = this.dataset.mode === 'product';
      this.owner = this.productMode ? this.closest('form') : this.closest('.product-item');
      if (!this.owner) return;
      this.abortController = new AbortController();
      this.requestNumber = this.requestNumber || 0;
      this.hide();
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.owner, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['value', 'data-variant-id', 'data-compare-price', 'data-currency', 'data-locale', 'data-label-template']
      });
      if (this.productMode) {
        ['change', 'input', 'variant:change', 'product:rerender'].forEach((eventName) => {
          this.owner.addEventListener(eventName, () => this.schedule(),
            { signal: this.abortController.signal });
        });
      }
      this.refresh();
    }

    disconnectedCallback() {
      this.requestNumber = (this.requestNumber || 0) + 1;
      this.abortController?.abort();
      this.observer?.disconnect();
      this.observer = null;
      this.lookupKey = null;
      clearTimeout(this.scheduled);
    }

    schedule() {
      clearTimeout(this.scheduled);
      this.scheduled = setTimeout(() => this.refresh(), 0);
    }

    hide() {
      if (this.style.display !== 'none') this.style.display = 'none';
    }

    showSavings(comparePrice, sellingPrice) {
      const difference = Math.round(comparePrice - sellingPrice);
      if (!Number.isFinite(comparePrice) || comparePrice <= 0 ||
          !Number.isFinite(sellingPrice) || sellingPrice < 0 || difference <= 0) {
        this.hide();
        return;
      }
      try {
        const money = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency', currency: this.dataset.currency || 'USD',
          minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(difference / 100);
        const labelKey = `${money}:${this.dataset.labelTemplate}`;
        if (this.labelKey !== labelKey) {
          const template = document.createElement('template');
          template.innerHTML = this.dataset.labelTemplate || 'Save __PV_DL_SAVINGS__';
          const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
          let replaced = false;
          while (walker.nextNode()) {
            const node = walker.currentNode;
            if (node.textContent.includes('__PV_DL_SAVINGS__')) {
              node.textContent = node.textContent.replaceAll('__PV_DL_SAVINGS__', money);
              replaced = true;
            }
          }
          if (!replaced) { this.hide(); return; }
          this.replaceChildren(template.content);
          this.labelKey = labelKey;
        }
        if (this.style.display !== '') this.style.display = '';
      } catch (_) {
        this.hide();
      }
    }

    async refresh() {
      if (!this.isConnected || !this.owner) return;
      if (this.productMode) {
        const controller = this.owner.pvDLPricingController;
        const priceElement = controller?.priceElement();
        const selection = controller?.selection();
        if (controller?.state !== 'ready' || !controller.result ||
            controller.key !== selection?.key ||
            priceElement?.dataset.variantId !== selection?.variantId) {
          this.hide();
          return;
        }
        this.showSavings(Number(priceElement.dataset.comparePrice), controller.result.price.priceInCents);
        return;
      }

      const priceHook = this.owner.querySelector('.dollarlabs_pricelist--product_price[data-custom-price-js]');
      const variantId = String(priceHook?.dataset.variantId || this.dataset.variantId || '');
      const comparePrice = Number(this.dataset.comparePrice);
      const key = `${variantId}:${comparePrice}:${this.dataset.currency}:${this.dataset.locale}:${this.dataset.labelTemplate}`;
      if (!variantId || !Number.isFinite(comparePrice) || comparePrice <= 0 || !sdkReady()) {
        this.lookupKey = null;
        this.requestNumber += 1;
        this.hide();
        return;
      }
      if (this.lookupKey === key) return;
      this.lookupKey = key;
      this.hide();
      const requestNumber = ++this.requestNumber;
      try {
        const sellingPrice = await cardPrice(variantId);
        if (!this.isConnected || requestNumber !== this.requestNumber || this.lookupKey !== key) return;
        this.showSavings(comparePrice, sellingPrice);
      } catch (_) {
        if (this.isConnected && requestNumber === this.requestNumber) this.hide();
      }
    }
  }

  customElements.define(elementName, PromoVetDLSavings);
  const refreshBadges = () => {
    document.querySelectorAll(elementName).forEach((badge) => {
      badge.lookupKey = null;
      badge.schedule();
    });
  };
  document.addEventListener('dollarlabs:pricing:ready', () => {
    cardPrices.clear();
    refreshBadges();
  });
  if (!sdkReady()) {
    const poll = setInterval(() => {
      if (sdkReady()) { clearInterval(poll); clearTimeout(deadline); refreshBadges(); }
    }, 200);
    const deadline = setTimeout(() => clearInterval(poll), 10000);
  }
})();
